/**
 * Load-test switch: `?avatar=stress` draws every avatar as a skinned stand-in mesh
 * (engine/src/stress.rs), so a crowd costs roughly what real OpenSim avatars would.
 *
 *   ?avatar=stress                         50k triangles, 8 draw calls, 60 bones each
 *   ?avatar=stress&tris=100000&parts=12&bones=120
 *   ?avatar=stress&lod=0                   every avatar at full detail (no distance LOD)
 *   ?avatar=stress&impostor_px=0           never draw far avatars as impostors (default:
 *                                          below 80 px tall on screen)
 *
 * The engine clamps the numbers to what it can draw; this only parses them.
 */
export interface StressOptions {
  triangles: number;
  parts: number;
  bones: number;
  /** Distance level of detail (default on). */
  lod: boolean;
  /** Avatars shorter than this on screen (pixels) become impostors; 0 = never. */
  impostorPx: number;
}

export const STRESS_DEFAULTS: StressOptions = {
  triangles: 50_000,
  parts: 8,
  bones: 60,
  lod: true,
  impostorPx: 80,
};

export function stressOptions(search: string): StressOptions | null {
  const params = new URLSearchParams(search);
  if (params.get('avatar') !== 'stress') return null;
  const int = (name: string, fallback: number): number => {
    const value = Number.parseInt(params.get(name) ?? '', 10);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  };
  return {
    triangles: int('tris', STRESS_DEFAULTS.triangles),
    parts: int('parts', STRESS_DEFAULTS.parts),
    bones: int('bones', STRESS_DEFAULTS.bones),
    lod: params.get('lod') !== '0',
    // 0 is meaningful here (off), so this one isn't read with `int`.
    impostorPx: params.has('impostor_px')
      ? Math.max(0, Number.parseFloat(params.get('impostor_px') ?? '') || 0)
      : STRESS_DEFAULTS.impostorPx,
  };
}

/** HUD text for `engine.lodCounts` (levels, then impostors): "LOD 2/59/66/0 · 25 imp". */
export function lodSummary(counts: ArrayLike<number>): string {
  const all = Array.from(counts);
  const impostors = all.length > 4 ? all.pop() : undefined;
  return `LOD ${all.join('/')}${impostors === undefined ? '' : ` · ${impostors} imp`}`;
}

/** "7.5M" / "120k" / "950": short enough for the HUD. */
export function compactCount(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)}k`;
  return `${Math.round(n)}`;
}
